import type { EventId, PlanEvent } from "../../../shared/types";
import {
  TIME_COL_W,
  HOUR_HEIGHT_PX,
  DAY_START_HOUR,
  DAY_END_HOUR,
  TOTAL_GRID_HEIGHT_PX,
} from "../utils/time-constants";
import { timeToMinutes, getEventPosition } from "../utils/time-positioning";

const SLOT_PX = (HOUR_HEIGHT_PX / 60) * 15;
const NOW_SNAP_THRESHOLD = 7; // minutes

/** Get current time in minutes, or null if outside grid */
function getNowMinutes(): number | null {
  const now = new Date();
  const m = now.getHours() * 60 + now.getMinutes();
  if (m < DAY_START_HOUR * 60 || m >= DAY_END_HOUR * 60) return null;
  return m;
}

/** Snap minutes to 15-min grid, with magnetic snap to "now". Shift = 1 min precision. */
export function snapMinutes(raw: number, freeSnap = false): number {
  if (freeSnap) return Math.round(raw);
  const nowM = getNowMinutes();
  if (nowM !== null && Math.abs(raw - nowM) <= NOW_SNAP_THRESHOLD) return nowM;
  return Math.round(raw / 15) * 15;
}

export function minutesToTime(minutes: number): string {
  const clamped = Math.max(
    DAY_START_HOUR * 60,
    Math.min(DAY_END_HOUR * 60, minutes),
  );
  if (clamped === DAY_END_HOUR * 60) return "24:00";
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function snapToPx(px: number, freeSnap = false): number {
  const rawMinutes = (px / HOUR_HEIGHT_PX) * 60 + DAY_START_HOUR * 60;
  const snapped = snapMinutes(rawMinutes, freeSnap);
  return ((snapped - DAY_START_HOUR * 60) / 60) * HOUR_HEIGHT_PX;
}

export function pxToMinutes(pxFromTop: number): number {
  return (pxFromTop / HOUR_HEIGHT_PX) * 60 + DAY_START_HOUR * 60;
}

export interface DraggingState {
  readonly event: PlanEvent;
  readonly ghostDate: string;
  readonly ghostTopPx: number;
  readonly durationMinutes: number;
  readonly freeSnap?: boolean;
}

export interface ResizingState {
  readonly event: PlanEvent;
  readonly edge: "top" | "bottom";
  readonly ghostTopPx: number;
  readonly ghostHeightPx: number;
  readonly newStartMinutes: number;
  readonly newEndMinutes: number;
}

export type UpdateFn = (
  id: EventId,
  updates: Partial<Pick<PlanEvent, "date" | "startTime" | "endTime">>,
) => void;

export function calculateDragDuration(event: PlanEvent): {
  topPx: number;
  durationMinutes: number;
} {
  const { topPx, heightPx } = getEventPosition(event.startTime, event.endTime);
  const durationMinutes = Math.round((heightPx / HOUR_HEIGHT_PX) * 60);
  return { topPx, durationMinutes };
}

export function calculateResizeDelta(
  deltaY: number,
  edge: "top" | "bottom",
  resizing: ResizingState,
  freeSnap = false,
): Partial<ResizingState> {
  const deltaMinutes = (deltaY / HOUR_HEIGHT_PX) * 60;
  const minSnap = freeSnap ? 1 : 15;
  if (edge === "bottom") {
    const startMinutes = timeToMinutes(resizing.event.startTime);
    const originalEnd = timeToMinutes(resizing.event.endTime);
    const snappedEnd = Math.max(DAY_START_HOUR * 60, Math.min(DAY_END_HOUR * 60, snapMinutes(originalEnd + deltaMinutes, freeSnap)));

    if (snappedEnd < startMinutes) {
      // Inversion : le bas passe au-dessus du début
      const newStart = Math.max(DAY_START_HOUR * 60, snappedEnd);
      const newEnd = startMinutes;
      const heightPx = ((newEnd - newStart) / 60) * HOUR_HEIGHT_PX;
      const topPx = ((newStart - DAY_START_HOUR * 60) / 60) * HOUR_HEIGHT_PX;
      return { ghostTopPx: topPx, ghostHeightPx: heightPx, newStartMinutes: newStart, newEndMinutes: newEnd };
    }

    const clampedEnd = Math.max(startMinutes + minSnap, snappedEnd);
    const heightPx = ((clampedEnd - startMinutes) / 60) * HOUR_HEIGHT_PX;
    return { ghostHeightPx: heightPx, newEndMinutes: clampedEnd };
  } else {
    const originalStart = timeToMinutes(resizing.event.startTime);
    const endMinutes = resizing.newEndMinutes;
    const snappedStart = Math.max(DAY_START_HOUR * 60, Math.min(DAY_END_HOUR * 60, snapMinutes(originalStart + deltaMinutes, freeSnap)));

    if (snappedStart > endMinutes) {
      // Inversion : le haut passe en dessous de la fin
      const newStart = endMinutes;
      const newEnd = Math.min(DAY_END_HOUR * 60, snappedStart);
      const heightPx = ((newEnd - newStart) / 60) * HOUR_HEIGHT_PX;
      const topPx = ((newStart - DAY_START_HOUR * 60) / 60) * HOUR_HEIGHT_PX;
      return { ghostTopPx: topPx, ghostHeightPx: heightPx, newStartMinutes: newStart, newEndMinutes: newEnd };
    }

    const clampedStart = Math.min(endMinutes - minSnap, snappedStart);
    const heightPx = ((endMinutes - clampedStart) / 60) * HOUR_HEIGHT_PX;
    const topPx = ((clampedStart - DAY_START_HOUR * 60) / 60) * HOUR_HEIGHT_PX;
    return {
      ghostTopPx: topPx,
      ghostHeightPx: heightPx,
      newStartMinutes: clampedStart,
    };
  }
}

export function resolveDragTarget(
  clientX: number,
  clientY: number,
  days: readonly string[],
  gridRef: React.RefObject<HTMLDivElement | null>,
): { ghostDate: string; gridY: number | null } {
  const el = document.elementFromPoint(clientX, clientY);
  const dayColEl = el?.closest("[data-daycol]") as HTMLElement | null;

  if (dayColEl?.dataset.daycol && days.includes(dayColEl.dataset.daycol)) {
    const ghostDate = dayColEl.dataset.daycol;
    const gridY = clientY - dayColEl.getBoundingClientRect().top;
    return { ghostDate, gridY };
  }

  if (!gridRef.current) return { ghostDate: days[0] ?? "", gridY: null };
  const rect = gridRef.current.getBoundingClientRect();
  const gridX = clientX - rect.left - TIME_COL_W;
  const colWidth = (rect.width - TIME_COL_W) / days.length;
  const colIndex = Math.max(
    0,
    Math.min(days.length - 1, Math.floor(gridX / colWidth)),
  );
  return { ghostDate: days[colIndex] ?? days[0] ?? "", gridY: null };
}

export function clampTopPx(snappedTopPx: number): number {
  return Math.max(0, Math.min(TOTAL_GRID_HEIGHT_PX - SLOT_PX, snappedTopPx));
}
